export default class DataHelper {
    static convertToString(value: any, dataType: string, configuration?: DataHelperConfiguration): string {
        let stringValue = null;

        switch (dataType) {
            case "Boolean":
                stringValue = value === true ? "True" : null;
                break;
            case "Integer":
                stringValue = this.convertNumberToString(value, configuration, false);
                break;
            case "Decimal":
                stringValue = this.convertNumberToString(value, configuration);
                break;
            case "Date":
                stringValue = this.convertDateToString(value, configuration, false, false);
                break;
            case "DateTime":
                stringValue = this.convertDateToString(value, configuration, true, false);
                break;
            case "DateTimeOffset":
                stringValue = this.convertDateToString(value, configuration);
                break;
            case "String":
                if (value !== null && value !== "") {
                    stringValue = String(value);
                }
                break;
            case "Structured":
                if (value !== null && value !== "") {
                    stringValue = JSON.stringify(value, (key, value) => value === null ? undefined : value, "\t");
                }
                break;
        }

        return stringValue;
    }

    static convertToNative(value: string, dataType: string, configuration?: DataHelperConfiguration): any {
        let nativeValue = null;

        switch (dataType) {
            case "Boolean":
                nativeValue = value === "True" ? true : false;
                break;
            case "Integer":
                nativeValue = this.convertStringToNumber(value, configuration);
                break;
            case "Decimal":
                nativeValue = this.convertStringToNumber(value, configuration);
                break;
            case "Date":
                nativeValue = this.convertStringToDate(value, configuration);
                break;
            case "DateTime":
                nativeValue = this.convertStringToDate(value, configuration);
                break;
            case "DateTimeOffset":
                nativeValue = this.convertStringToDate(value, configuration);
                break;
            case "String":
                if (value !== null && value !== "") {
                    nativeValue = value;
                }
                break;
            case "Structured":
                if (value !== null && value !== "") {
                    nativeValue = JSON.parse(value);
                }
                break;
        }

        return nativeValue;
    }

    static isNumeric(value: any): boolean {
        return !isNaN(value - parseFloat(value));
    }

    static convertStringToNumber(value: string, configuration?: DataHelperConfiguration): number {
        let config = this.initConfiguration(configuration);

        let nativeValue: any = null;

        if (value !== null && value !== "") {
            let formattedValue = value;

            if (config.thousandsSeparator) {
                formattedValue = formattedValue.replace(new RegExp("\\" + config.thousandsSeparator, "g"), "")
            }

            formattedValue = formattedValue.replace(config.decimalSeparator, ".");

            if (this.isNumeric(formattedValue)) {
                nativeValue = parseFloat(formattedValue);
            } else {
                nativeValue = value;
            }
        }

        return nativeValue;
    }

    static convertNumberToString(value: number, configuration?: DataHelperConfiguration, isDecimalPart: boolean = true) {
        let config = this.initConfiguration(configuration);

        let stringValue: string = null;

        if (value != null) {
            if (this.isNumeric(value)) {
                stringValue = String(value).replace(/,/g, "");

                // format integer and decimal parts
                let valueParts: string[] = stringValue.split(".");
                let integerPart: string = valueParts[0];
                let decimalPart: string = "";
                if (valueParts.length > 1) {
                    decimalPart = valueParts[1];
                }

                // Set thousands separator
                if (integerPart.length > 3 && integerPart.substring(0, integerPart.length - 3) !== "-") {
                    integerPart = integerPart.substring(0, integerPart.length - 3) + config.thousandsSeparator + integerPart.substring(integerPart.length - 3);
                }

                // Set millions separator
                if (integerPart.length > 7 && integerPart.substring(0, integerPart.length - 7) !== "-") {
                    integerPart = integerPart.substring(0, integerPart.length - 7) + config.thousandsSeparator + integerPart.substring(integerPart.length - 7);
                }

                stringValue = integerPart;
                if (config.decimalCount !== 0 && isDecimalPart) {
                    stringValue += config.decimalSeparator + decimalPart.padEnd(config.decimalCount, "0");
                }
            } else {
                stringValue = String(value);
            }
        }

        return stringValue;
    }

    static convertStringToDate(value: string, configuration?: DataHelperConfiguration): string {
        let config = this.initConfiguration(configuration);

        let nativeValue: string = null;

        if (value != null && value != "") {
            let dateTimeParts = null;

            switch (config.dateFormat) {
                case "DayFirst":
                    dateTimeParts = this.getDayFirstDateTimeParts(value);
                    break;
                case "MonthFirst":
                    dateTimeParts = this.getMonthFirstDateTimeParts(value);
                    break;
                default:
                case "ISO":
                    dateTimeParts = this.getISODateTimeParts(value);
                    break;
            }

            if (dateTimeParts) {
                nativeValue = dateTimeParts.year + "-" + dateTimeParts.month + "-" + dateTimeParts.day + "T" + dateTimeParts.hour + ":" + dateTimeParts.minutes + ":" + dateTimeParts.seconds;

                if (dateTimeParts.offset) {
                    nativeValue += dateTimeParts.offset;
                }
            }
        }

        return nativeValue;
    }

    static convertDateToString(value: string, configuration?: DataHelperConfiguration, isTimePart: boolean = true, isOffsetPart: boolean = true): string {
        let config = this.initConfiguration(configuration);

        let stringValue: string = null;

        let dateTimeParts = this.getISODateTimeParts(value);
        if (dateTimeParts) {
            switch (config.dateFormat) {
                case "DayFirst":
                    stringValue = dateTimeParts.day + "/" + dateTimeParts.month + "/" + dateTimeParts.year;

                    if (isTimePart) {
                        stringValue += " " + dateTimeParts.hour + ":" + dateTimeParts.minutes;
                    }

                    if (isOffsetPart) {
                        stringValue += " " + dateTimeParts.offset;
                    }
                    break;
                case "MonthFirst":
                    stringValue = dateTimeParts.month + "/" + dateTimeParts.day + "/" + dateTimeParts.year;

                    if (isTimePart) {
                        stringValue += " " + dateTimeParts.hour + ":" + dateTimeParts.minutes;
                    }

                    if (isOffsetPart) {
                        stringValue += " " + dateTimeParts.offset;
                    }
                    break;
                case "ISO":
                default:
                    stringValue = dateTimeParts.year + "-" + dateTimeParts.month + "-" + dateTimeParts.day;

                    if (isTimePart) {
                        stringValue += "T" + dateTimeParts.hour + ":" + dateTimeParts.minutes + ":" + dateTimeParts.seconds;
                    }

                    if (isOffsetPart && dateTimeParts.offset) {
                        stringValue += dateTimeParts.offset;
                    }
                    break;
            }
        }

        return stringValue;
    }

    static getDatePart(value: string): string {
        let stringValue: string = null;

        let dateTimeParts = this.getISODateTimeParts(value);
        if (dateTimeParts) {
            stringValue = dateTimeParts.year + "-" + dateTimeParts.month + "-" + dateTimeParts.day;
        }

        return stringValue;
    }

    static getTimePart(value: string): string {
        let stringValue: string = null;

        let dateTimeParts = this.getISODateTimeParts(value);
        if (dateTimeParts) {
            stringValue = dateTimeParts.hour + ":" + dateTimeParts.minutes;
        }

        return stringValue;
    }

    static getOffsetPart(value: string): string {
        let stringValue: string = null;

        let dateTimeParts = this.getISODateTimeParts(value);
        if (dateTimeParts) {
            stringValue = dateTimeParts.offset;
        }

        return stringValue;
    }

    static getISODateTimeParts(value: string): any {
        if (!value) {
            return null;
        }

        if (!this.isValidDateTime(value)) {
            return null;
        }

        let [datePart, timePart] = value.split("T");
        let [year, month, day] = datePart.match(/\d+/g);
        let [time, offset] = timePart ? timePart.split(/(?=[+-])/) : ["00:00:00", null];

        let hour = timePart ? timePart.split(":")[0] : null;
        let minutes = timePart ? timePart.split(":")[1] : null;
        let seconds = timePart ? timePart.split(":")[2] : null;

        if (seconds) {
            if (seconds.includes("+")) {
                seconds = seconds.split("+")[0];
            } else if (seconds.includes("-")) {
                seconds = seconds.split("-")[0];
            }
        }

        return { datePart: datePart, timePart: timePart, year: year, month: month, day: day, hour: hour ?? "00", minutes: minutes ?? "00", seconds: seconds ?? "00", offset: offset ?? null };
    }

    static getDayFirstDateTimeParts(value: string): any {
        if (!value) {
            return null;
        }

        let [datePart, timePart, offset] = value.split(" ");
        let [day, month, year] = datePart.match(/\d+/g);

        let hour = timePart ? timePart.split(":")[0] : null;
        let minutes = timePart ? timePart.split(":")[1] : null;
        let seconds = timePart ? timePart.split(":")[2] : null;

        return { datePart: datePart, timePart: timePart, year: year, month: month, day: day, hour: hour ?? "00", minutes: minutes ?? "00", seconds: seconds ?? "00", offset: offset ?? null };
    }

    static getMonthFirstDateTimeParts(value: string): any {
        if (!value) {
            return null;
        }

        let [datePart, timePart, offset] = value.split(" ");
        let [month, day, year] = datePart.match(/\d+/g);

        let hour = timePart ? timePart.split(":")[0] : null;
        let minutes = timePart ? timePart.split(":")[1] : null;
        let seconds = timePart ? timePart.split(":")[2] : null;

        return { datePart: datePart, timePart: timePart, year: year, month: month, day: day, hour: hour ?? "00", minutes: minutes ?? "00", seconds: seconds ?? "00", offset: offset ?? null };
    }

    static isValidDateTime(value: any): boolean {
        // Reference: https://www.delftstack.com/howto/javascript/javascript-validate-date/
        let isValidDate = Date.parse(value);
        return !isNaN(isValidDate);
    }

    static getLocalOffset(): string {
        let local = new Date();
        let tzo = -local.getTimezoneOffset();
        let dif = tzo >= 0 ? "+" : "-";
        return (dif + String(tzo / 60).padStart(2, "0") + ":" + String(tzo % 60).padStart(2, "0"));
    }

    static initConfiguration(configuration: DataHelperConfiguration) {
        let culture = configuration?.culture ?? "default";

        let config = this.getConfigurationByCulture(culture);

        if (configuration?.decimalSeparator != null && configuration?.decimalSeparator != undefined) {
            config.decimalSeparator = configuration.decimalSeparator;
        }

        if (configuration?.decimalCount) {
            config.decimalCount = configuration.decimalCount;
        }

        if (configuration?.thousandsSeparator != null && configuration?.thousandsSeparator != undefined) {
            config.thousandsSeparator = configuration.thousandsSeparator;
        }

        return config;
    }

    static getConfigurationByCulture(culture: string): any {
        switch (culture) {
            case "en-GB":
            case "it-IT":
            case "cs-CZ":
            case "da-DK":
            case "de-DE":
            case "el-GR":
            case "es-ES":
            case "fi-FI":
            case "fr-FR":
            case "hu-HU":
            case "ja-JP":
            case "ko-KR":
            case "ms-MY":
            case "pl-PL":
            case "ro-RO":
            case "ru-RU":
            case "sk-SK":
            case "sv-SE":
            case "th-TH":
            case "tr-TR":
            case "zh-CN":
                return {
                    culture: culture,
                    decimalSeparator: ",",
                    decimalCount: 2,
                    thousandsSeparator: ".",
                    dateFormat: "DayFirst"
                }
            case "en-US":
                return {
                    culture: culture,
                    decimalSeparator: ".",
                    decimalCount: 2,
                    thousandsSeparator: ",",
                    dateFormat: "MonthFirst"
                }
            case "default":
            default:
                return {
                    culture: "default",
                    decimalSeparator: ".",
                    decimalCount: 2,
                    thousandsSeparator: "",
                    dateFormat: "ISO"
                }
        }
    }
}

export interface DataHelperConfiguration {
    culture?: string;
    decimalSeparator?: string;
    decimalCount?: number;
    thousandsSeparator?: string;
    dateFormat?: string;
}